import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Wrapper for the "Image + Text" section panel (sections/custom-image-text.liquid).
 *
 * The slideshow itself is Horizon's <slideshow-component>; this element only
 * - keeps the shared caption bar in sync with the active slide by listening to the
 *   bubbling `slideshow:select` event, and
 * - honours prefers-reduced-motion: an autoplaying slideshow starts paused (the user
 *   can still press play; hover/leave will not restart it while paused).
 *
 * @extends {Component<{}>}
 */
class CustomImageTextComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('slideshow:select', this.#handleSelect);

    const slideshow = this.querySelector('slideshow-component');
    if (slideshow?.hasAttribute('autoplay') && prefersReducedMotion()) {
      // `paused` is read by Slideshow#resume(), so setting the attribute works whether or not
      // the slideshow module has upgraded the element yet.
      slideshow.setAttribute('paused', '');
      if ('suspend' in slideshow && typeof slideshow.suspend === 'function') slideshow.suspend();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('slideshow:select', this.#handleSelect);
  }

  /** @param {Event & { detail?: { index?: number } }} event */
  #handleSelect = (event) => {
    const index = event.detail?.index;
    if (typeof index !== 'number') return;

    this.querySelectorAll('[data-cit-caption]').forEach((caption, i) => {
      caption.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });
  };
}

if (!customElements.get('custom-image-text-component')) {
  customElements.define('custom-image-text-component', CustomImageTextComponent);
}
